import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { propertyFormSchema } from "@/modules/Properties/schema/propertyFormSchema";
import { Check, Plus, Tags, X } from "lucide-react";
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const predefinedFeatures = [
    "parking",
    "swimmingPool",
    "garden",
    "gym",
    "securitySystem",
    "airConditioning",
    "heating",
    "elevator",
    "balcony",
    "storage",
    "petFriendly",
    "furnished",
    "smartHome",
    "highSpeedInternet",
    "laundry"
] as const;

interface PropertyFeaturesProps {
    form: UseFormReturn<z.infer<typeof propertyFormSchema>>;
    isEditing: boolean;
}

export const PropertyFeatures = ({ form, isEditing }: PropertyFeaturesProps) => {
    const { t } = useTranslation();
    const [newFeature, setNewFeature] = useState("");
    const [openCommand, setOpenCommand] = useState(false);

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-lg font-medium flex items-center gap-2">
                    <Tags className="h-5 w-5 text-primary" />
                    {t("properties.form.features.title")}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <FormField
                    control={form.control}
                    name="features"
                    render={({ field }) => (
                        <FormItem>
                            <div className="space-y-4">
                                {isEditing && (
                                    <div className="flex gap-2">
                                        <div className="flex-1">
                                            <Popover open={openCommand} onOpenChange={setOpenCommand}>
                                                <PopoverTrigger asChild>
                                                    <div className="relative">
                                                        <Input
                                                            value={newFeature}
                                                            onChange={(e) => setNewFeature(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") {
                                                                    e.preventDefault();
                                                                    if (!newFeature.trim()) return;

                                                                    const normalizedFeature = newFeature.trim();
                                                                    if (field.value?.includes(normalizedFeature)) return;

                                                                    field.onChange([...(field.value || []), normalizedFeature]);
                                                                    setNewFeature("");
                                                                    setOpenCommand(false);
                                                                }
                                                            }}
                                                            placeholder={t("properties.form.features.featuresPlaceholder")}
                                                            className="pl-8"
                                                        />
                                                        <Tags className="h-4 w-4 text-muted-foreground absolute left-2 top-1/2 transform -translate-y-1/2" />
                                                    </div>
                                                </PopoverTrigger>
                                                <PopoverContent className="p-0" align="start">
                                                    <Command>
                                                        <CommandInput
                                                            placeholder={t("properties.form.features.searchFeatures")}
                                                            value={newFeature}
                                                            onValueChange={setNewFeature}
                                                        />
                                                        <CommandList>
                                                            <CommandEmpty>
                                                                {t("properties.form.features.noFeatures")}
                                                                {newFeature && (
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="mt-2 w-full justify-start"
                                                                        onClick={() => {
                                                                            if (!newFeature.trim()) return;
                                                                            const normalizedFeature = newFeature.trim();
                                                                            if (field.value?.includes(normalizedFeature)) return;
                                                                            field.onChange([...(field.value || []), normalizedFeature]);
                                                                            setNewFeature("");
                                                                            setOpenCommand(false);
                                                                        }}
                                                                    >
                                                                        <Plus className="mr-2 h-4 w-4" />
                                                                        {t("properties.form.features.addFeature")}: "{newFeature}"
                                                                    </Button>
                                                                )}
                                                            </CommandEmpty>
                                                            <CommandGroup>
                                                                {predefinedFeatures
                                                                    .filter(feature =>
                                                                        t(`properties.form.features.predefined.${feature}`).toLowerCase()
                                                                            .includes(newFeature.toLowerCase()) &&
                                                                        !field.value?.includes(feature)
                                                                    )
                                                                    .map((feature) => (
                                                                        <CommandItem
                                                                            key={feature}
                                                                            value={feature}
                                                                            onSelect={() => {
                                                                                field.onChange([...(field.value || []), feature]);
                                                                                setNewFeature("");
                                                                                setOpenCommand(false);
                                                                            }}
                                                                        >
                                                                            <Check
                                                                                className={cn(
                                                                                    "mr-2 h-4 w-4",
                                                                                    field.value?.includes(feature)
                                                                                        ? "opacity-100"
                                                                                        : "opacity-0"
                                                                                )}
                                                                            />
                                                                            {t(`properties.form.features.predefined.${feature}`)}
                                                                        </CommandItem>
                                                                    ))}
                                                            </CommandGroup>
                                                        </CommandList>
                                                    </Command>
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                        <Button
                                            type="button"
                                            onClick={() => {
                                                if (!newFeature.trim()) return;
                                                const normalizedFeature = newFeature.trim();
                                                if (field.value?.includes(normalizedFeature)) return;
                                                field.onChange([...(field.value || []), normalizedFeature]);
                                                setNewFeature("");
                                            }}
                                            disabled={!newFeature.trim()}
                                        >
                                            <Plus className="h-4 w-4" />
                                        </Button>
                                    </div>
                                )}

                                <div className="flex flex-wrap gap-2">
                                    {(!field.value || field.value.length === 0) ? (
                                        <p className="text-sm text-muted-foreground">
                                            {t("properties.form.features.noFeaturesAdded")}
                                        </p>
                                    ) : (
                                        field.value.map((feature) => (
                                            <Badge
                                                key={feature}
                                                variant="secondary"
                                                className="flex items-center gap-1"
                                            >
                                                {predefinedFeatures.includes(feature as any)
                                                    ? t(`properties.form.features.predefined.${feature}`)
                                                    : feature}
                                                {isEditing && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            field.onChange(field.value?.filter(f => f !== feature));
                                                        }}
                                                        className="ml-1 hover:text-destructive focus:outline-none"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                )}
                                            </Badge>
                                        ))
                                    )}
                                </div>
                            </div>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </CardContent>
        </Card>
    );
}; 